/**
 * Alignement export BEAM sur l’écran Portefeuille (répartition Synthèse + tableau Détail).
 * Logique calquée sur Portefeuille.jsx (portfolioDistributionData + colonnes du tableau).
 */

export function totalApportsEur(apportsList) {
  if (!Array.isArray(apportsList)) return null;
  let t = 0;
  for (const a of apportsList) {
    if (!a || a.actif === false) continue;
    const m = parseFloat(String(a.montant));
    if (!Number.isNaN(m) && m < 0) t += Math.abs(m);
  }
  return t > 0 ? t : null;
}

export function buildEnrichmentMapFromBulk(items) {
  const m = new Map();
  if (!Array.isArray(items)) return m;
  for (const item of items) {
    const sym = (item.symbol || '').toString().toUpperCase();
    if (sym && item.categorie_name) m.set(sym, item.categorie_name);
  }
  return m;
}

/**
 * @param {Array} positions
 * @param {Array} categories - liste API /categories (actives)
 * @param {Map} enrichmentMap - symbole -> nom catégorie affichée
 * @param {object|null} account - objet /tws/account (net_liquidation, gross_position_value)
 * @returns {{ byCategory: Array<{name: string, value: number}> }}
 */
export function computePortfolioDistributionByCategory(positions, categories, enrichmentMap, account) {
  if (!Array.isArray(positions) || positions.length === 0) {
    return { byCategory: [] };
  }
  const cats = Array.isArray(categories) ? categories.filter((c) => c.is_active !== false) : [];

  const categoryMap = new Map();
  cats.forEach((cat) => {
    categoryMap.set(cat.id, cat);
  });

  const symbolToCategory = new Map();
  if (enrichmentMap && typeof enrichmentMap.forEach === 'function') {
    enrichmentMap.forEach((categoryName, symbol) => {
      const category = cats.find((c) => c.name === categoryName);
      if (category) {
        const s = (symbol || '').toString();
        symbolToCategory.set(s, category);
        symbolToCategory.set(s.toUpperCase(), category);
      }
    });
  }

  const accountTotalValue =
    account?.gross_position_value?.value ?? account?.net_liquidation?.value ?? null;

  let totalRawValue = 0;
  positions.forEach((position) => {
    const marketValue = position.marketValue || 0;
    if (marketValue > 0) totalRawValue += marketValue;
  });

  let normalizationRatio = 1.0;
  if (accountTotalValue != null && totalRawValue > 0) {
    normalizationRatio = accountTotalValue / totalRawValue;
  }

  const categoryValues = new Map();
  positions.forEach((position) => {
    const marketValue = position.marketValue || 0;
    if (marketValue <= 0) return;
    const valueInEUR = marketValue * normalizationRatio;
    const ps = (position.symbol || '').toString();
    const category =
      symbolToCategory.get(ps) || symbolToCategory.get(ps.toUpperCase());
    if (category) {
      const currentValue = categoryValues.get(category.name) || 0;
      categoryValues.set(category.name, currentValue + valueInEUR);
    } else {
      const currentValue = categoryValues.get('Non catégorisé') || 0;
      categoryValues.set('Non catégorisé', currentValue + valueInEUR);
    }
  });

  const byCategory = Array.from(categoryValues.entries())
    .map(([name, value]) => {
      const roundedValue = Math.round(value * 100) / 100;
      if (Number.isNaN(roundedValue) || roundedValue <= 0) return null;
      return { name, value: roundedValue };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);

  return { byCategory };
}

function formatCurrencyMd(value, currency = 'EUR', decimals = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const sym = currency === 'USD' ? 'US$' : currency === 'CAD' ? 'C$' : '€';
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
  return `${formatted} ${sym}`;
}

/**
 * Tableau Markdown aligné sur « Détail du portefeuille » (colonnes Portefeuille.jsx).
 */
export function buildPortfolioDetailMarkdownTable(positions, enrichmentMap) {
  if (!Array.isArray(positions) || positions.length === 0) {
    return '_Aucune position._\n';
  }
  const headers = [
    'Ticker',
    'Nom',
    'Catégorie',
    'Type',
    'Quantité',
    'Prix moyen',
    'Valeur initiale',
    'Prix marché',
    'Valeur marché',
    'P&L non réalisé',
    'P&L %',
  ];
  const esc = (s) =>
    String(s ?? '')
      .replace(/\|/g, '\\|')
      .replace(/\n/g, ' ');
  const rows = [headers.join(' | '), headers.map(() => '---').join(' | ')];

  const qtyFmt = (v) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      Number(v) || 0
    );

  for (const row of positions) {
    const sym = row.symbol || '—';
    const name = row.name || '—';
    const su = (sym || '').toString();
    const cat =
      enrichmentMap && typeof enrichmentMap.get === 'function'
        ? enrichmentMap.get(su) || enrichmentMap.get(su.toUpperCase()) || '—'
        : '—';
    const secType = row.secType || '—';
    const qty = qtyFmt(row.position);
    const ccy = row.currency || 'USD';
    const avgCost = row.avgCost;
    const mpx = row.marketPrice;
    const mval = row.marketValue;
    const unreal = row.unrealizedPNL;
    const initialVal = (Number(avgCost) || 0) * (Number(row.position) || 0);
    let pnlPct = '—';
    const initAbs = Math.abs(Number(row.position) || 0) * (Number(avgCost) || 0);
    if (initAbs > 0 && unreal != null) {
      const percent = (Number(unreal) / initAbs) * 100;
      pnlPct = `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(percent)} %`;
    }
    rows.push(
      [
        esc(sym),
        esc(name),
        esc(cat),
        esc(secType),
        esc(qty),
        esc(formatCurrencyMd(avgCost, ccy, 2)),
        esc(formatCurrencyMd(initialVal, ccy, 2)),
        esc(formatCurrencyMd(mpx, ccy, 2)),
        esc(formatCurrencyMd(mval, ccy, 2)),
        esc(formatCurrencyMd(unreal, ccy, 2)),
        esc(pnlPct),
      ].join(' | ')
    );
  }
  return `${rows.join('\n')}\n`;
}
